<script setup>
  import { inject, provide, ref, toRef, computed, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { ChevronDown, EyeOff, Funnel, Play } from "lucide-vue-next";

  import { Card, CardContent, CardHeader } from "@/components/ui/card";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { Badge } from "@/components/ui/badge";
  import { Switch } from "@/components/ui/switch";
  import { Tooltip } from "@/components/ui/tooltip";
  import { AiButton, AiCardReviewKey, AiReviewArea, useAiCardReview } from "@/components/episto-ui/ai";

  import { useCurrentCardStore } from "@stores/survey/currentCard/currentCard";
  import { useAiWritingToolStore } from "@stores/aiWritingTool";
  import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
  import { useConditionsSentence } from "@utils/composables/useConditionsSentence";
  import CardTypePickerDialog from "../CardTypePickerDialog.vue";
  import { CARD_ICONS } from "@utils/constants/cardCatalog";
  import CurrentCardBody from "./CurrentCardBody.vue";
  import ConditionsGroupsField from "./Conditions/ConditionsGroupsField.vue";
  import ConditionsSentence from "./Conditions/ConditionsSentence.vue";
  import DeleteCardIcon from "../DeleteCardIcon.vue";

  const currentCardStore = useCurrentCardStore();
  const { currentSubType, computedCardFields, cardPane, currentConditions, currentIsHidden } =
    storeToRefs(currentCardStore);
  const { setCardPane, setCurrentCardHidden } = currentCardStore;

  const props = defineProps({
    currentCard: { type: Object, required: true },
    errorsByCard: { type: Object, required: true },
    currentIndex: { type: Number, required: true },
    currentToken: { type: String, required: true },
  });

  const cardErrors = (token) => props.errorsByCard?.[token] ?? [];

  const aiCardReview = useAiCardReview(useAiWritingToolStore().generateMany);
  provide(AiCardReviewKey, aiCardReview);
  const {
    mode: aiMode,
    actionLabel: aiActionLabel,
    showOriginal: aiShowOriginal,
    hasListFields: aiHasListFields,
    readonly: isCardBusy,
  } = aiCardReview;

  watch(
    () => props.currentToken,
    () => aiCardReview.reset(),
  );

  const { currentLocale, locales } = storeToRefs(useSurveyLocalesStore());
  const surveyLanguages = computed(() => [...new Set((locales.value ?? []).map((locale) => locale.language))]);
  const currentQuestion = computed(() => props.currentCard.specs?.question?.[currentLocale.value?.token] ?? "");
  const isGenerateAvailable = computed(() => ["q_single", "q_multiple", "q_ranking"].includes(props.currentCard.type));
  const isGenerateDisabled = computed(() => currentQuestion.value.length <= 5);
  const onAiSelect = (payload) =>
    aiCardReview.run(payload.action === "generate" ? { ...payload, question: currentQuestion.value } : payload);

  const { groupsSegments } = useConditionsSentence();
  const isConditionsPane = computed(() => cardPane.value === "conditions");
  const hasConditions = computed(() => currentConditions.value.length > 0);
  const showBanner = computed(() => isConditionsPane.value || hasConditions.value || currentIsHidden.value);
  const conditionsSentence = computed(() => groupsSegments(currentConditions.value));
  const toggleConditionsPane = () => setCardPane(isConditionsPane.value ? "content" : "conditions");

  const ruleForm = toRef(props, "currentCard");
  const typeIcon = computed(() => CARD_ICONS[props.currentCard.type] ?? CARD_ICONS.unknown);
  const nameError = ref(null);
  const dialogOpen = ref(false);

  const validateName = (value) => {
    const matcher = /[^(\w)]/;
    if (matcher.test(value)) {
      nameError.value = "Can't contain any special characters";
    } else {
      nameError.value = null;
    }
  };

  const eventBus = inject("editEventBus");
  const handleCommand = (command, token) => eventBus.emit({ command, token });
  const handleUpdate = (command, value) => eventBus.emit({ command, value });

  const onNameInput = (value) => {
    validateName(value);
    handleUpdate("changeName", value);
  };

  const handleChangeType = (subType) => handleUpdate("changeType", subType);
</script>

<template>
  <Card class="flex flex-col max-h-[calc(100vh-100px)] overflow-hidden p-4 shadow-xs">
    <div
      v-if="showBanner"
      class="-mx-4 -mt-4 mb-3 flex min-h-9 shrink-0 items-start overflow-hidden rounded-t-xl border-b text-13"
      :class="currentIsHidden ? 'border-border bg-muted' : 'border-episto-teal/25 bg-episto-teal/8'">
      <button
        type="button"
        class="flex min-w-0 flex-1 items-start gap-2.5 py-2 pl-4 text-left transition-colors"
        :class="currentIsHidden ? 'hover:bg-secondary' : 'hover:bg-episto-teal/12'"
        data-cy="card-conditions-banner"
        @click="toggleConditionsPane">
        <EyeOff
          v-if="currentIsHidden"
          class="mt-0.5 size-4 shrink-0 text-muted-foreground"
          :stroke-width="1.8" />
        <Funnel
          v-else
          class="mt-0.5 size-4 shrink-0 text-episto-teal-700"
          :stroke-width="1.8" />
        <span class="line-clamp-5 min-w-0 flex-1 leading-5">
          <span
            v-if="currentIsHidden"
            class="text-foreground-secondary">
            This card is disabled and never shown
          </span>
          <ConditionsSentence
            v-else-if="hasConditions"
            :segments="conditionsSentence"
            prefix="Shown if" />
          <span
            v-else
            class="text-foreground-secondary">
            Shown to everyone
          </span>
        </span>
        <span
          v-if="!currentIsHidden"
          class="shrink-0 pr-4 font-semibold text-episto-teal-700">
          {{ isConditionsPane ? "Done" : "Edit" }}
        </span>
      </button>
      <button
        v-if="currentIsHidden"
        type="button"
        class="shrink-0 py-2 pr-4 pl-2 font-semibold text-primary hover:underline"
        data-cy="card-enable-button"
        @click="setCurrentCardHidden(false)">
        Enable
      </button>
    </div>

    <CardHeader class="flex-shrink-0 p-0 pb-3">
      <div class="flex items-center justify-between gap-4">
        <div class="flex items-center gap-3 flex-1 min-w-0">
          <Button
            variant="outline"
            class="h-8 shrink-0 gap-1.5 rounded-lg text-xs font-medium text-primary"
            @click="dialogOpen = true">
            <component
              :is="typeIcon"
              class="size-3.5 text-muted-foreground"
              :stroke-width="1.8" />
            {{ currentSubType }}
            <ChevronDown class="size-3.5 text-muted-foreground" />
          </Button>
          <CardTypePickerDialog
            v-model:open="dialogOpen"
            title="Change card type"
            description="Select a new card type"
            action-label="Change type"
            :initial-key="currentCard.type"
            @select="handleChangeType" />
          <div class="flex-1 min-w-0">
            <Input
              v-model="ruleForm.name"
              placeholder="Card name"
              class="rounded-lg text-13 font-medium text-primary"
              @input="onNameInput($event.target.value)" />
            <p
              v-if="nameError"
              class="text-xs text-destructive mt-1">
              {{ nameError }}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-1 shrink-0">
          <AiButton
            variant="card"
            :disabled="isCardBusy"
            :busy="isCardBusy"
            :show-generate="isGenerateAvailable"
            :generate-disabled="isGenerateDisabled"
            :show-emoji="aiHasListFields"
            :languages="surveyLanguages"
            :default-language="currentLocale?.language"
            @select="onAiSelect" />
          <Tooltip :content="isConditionsPane ? 'Back to content' : 'Display logic'">
            <Button
              variant="ghost"
              size="icon"
              :class="[
                currentIsHidden ? 'text-foreground-subtle' : hasConditions && 'text-episto-teal-700',
                isConditionsPane && 'bg-secondary',
              ]"
              data-cy="card-conditions-toggle"
              @click="toggleConditionsPane">
              <Funnel class="h-4 w-4" />
            </Button>
          </Tooltip>
          <Tooltip content="Preview from this card">
            <Button
              variant="ghost"
              size="icon"
              @click="handleCommand('playFromCard', currentToken)">
              <Play class="h-4 w-4" />
            </Button>
          </Tooltip>
          <Tooltip content="Delete card">
            <span class="inline-flex">
              <DeleteCardIcon
                :card="currentCard"
                :card-token="currentToken" />
            </span>
          </Tooltip>
        </div>
      </div>
    </CardHeader>
    <CardContent class="flex-1 flex flex-col min-h-0 p-0">
      <div
        v-if="isConditionsPane"
        class="-m-1 flex-1 overflow-y-auto">
        <div class="p-1">
          <ConditionsGroupsField
            :conditions-groups="currentConditions"
            :disable-all="currentIsHidden" />
          <label class="mt-4 flex cursor-pointer items-center gap-3 border-t pt-3 text-13">
            <Switch
              :model-value="currentIsHidden"
              size="sm"
              data-cy="card-hidden-toggle"
              @update:model-value="setCurrentCardHidden" />
            <span class="min-w-0 flex-1">
              <span class="text-primary">Disable this card</span>
              <span class="text-foreground-subtle">
                Hidden from every respondent, the conditions above are ignored.</span
              >
            </span>
          </label>
        </div>
      </div>
      <template v-else>
        <AiReviewArea
          class="mb-3"
          :mode="aiMode"
          :label="`${aiActionLabel}…`"
          :show-original="aiShowOriginal"
          @accept="aiCardReview.accept()"
          @toggle="aiCardReview.toggleOriginal()"
          @retry="aiCardReview.retry()"
          @cancel="aiCardReview.cancel()" />
        <div class="space-y-2 mb-2">
          <Badge
            v-for="error in cardErrors(currentCard.token)"
            :key="`error-${error}`"
            variant="destructive"
            class="whitespace-normal text-wrap block w-fit">
            {{ error }}
          </Badge>
        </div>
        <CurrentCardBody
          :fields="computedCardFields"
          :current-card="currentCard" />
      </template>
    </CardContent>
  </Card>
</template>
